/**
 * PrivacyConsentGate - Avviso bloccante post-login per la presa visione
 * dell'informativa privacy aggiornata (data_presa_visione precedente alla soglia
 * configurata lato backend). Montato a livello globale in App.js: intercetta sia
 * il login da pagina che da modale, indipendentemente dalla rotta corrente.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';
import { getPresaVisioneStatus, getAnagraficaProfile, saveAnagraficaCompleta } from '../../services/profileService';
import { PRIVACY } from '../../utils/privacyConstants';
import PrivacyModal from './PrivacyModal';

export default function PrivacyConsentGate() {
  const { user, isAuthReady } = useAuth();
  const { openModal } = useModal();

  const [checking, setChecking] = useState(true);
  const [needsConsentUpdate, setNeedsConsentUpdate] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Verifica lo stato di presa visione appena l'utente è autenticato.
  // Tenuto in stato "checking" finché la verifica non risolve, per evitare
  // che la pagina sottostante compaia per un istante prima dell'eventuale gate.
  useEffect(() => {
    if (!isAuthReady) return;
    if (!user?.idAnagrafico) { setChecking(false); return; }

    let cancelled = false;
    setChecking(true);
    (async () => {
      const res = await getPresaVisioneStatus();
      if (cancelled) return;
      setNeedsConsentUpdate(!!(res.success && res.data?.needsConsentUpdate));
      setChecking(false);
    })();

    return () => { cancelled = true; };
  }, [isAuthReady, user?.idAnagrafico]);

  const handleOpenPrivacyModal = useCallback(() => {
    openModal({ id: 'privacy-modal', component: PrivacyModal, options: { size: 'large' } });
  }, [openModal]);

  const handleSubmit = useCallback(async () => {
    if (!checked || !user?.idAnagrafico) return;
    setSubmitting(true);
    setSubmitError(null);

    const profileRes = await getAnagraficaProfile(user.idAnagrafico);
    if (!profileRes.success) {
      setSubmitError(profileRes.message || 'Impossibile recuperare i dati del profilo. Riprova.');
      setSubmitting(false);
      return;
    }

    const contatto = profileRes.data.contatto || {};
    const recapiti = contatto.recapiti || {};
    const impieghi = (contatto.impieghi || []).map(imp => {
      const entry = {
        id_ente:     parseInt(imp.id_ente || imp.idEnte),
        ruolo:       imp.ruolo || '',
        ufficio:     imp.ufficio || '',
        tipo:        imp.tipo_impiego || imp.tipo || '',
        data_inizio: imp.data_inizio || imp.dataInizio || '',
        data_fine:   imp.data_fine  || imp.dataFine  || '',
      };
      if (imp.id) entry.id = parseInt(imp.id);
      return entry;
    });

    const registrazione = {
      id:             parseInt(contatto.id),
      id_utente_sito: String(user.id),
      nome:            contatto.nome           || '',
      cognome:         contatto.cognome         || '',
      codice_fiscale:  contatto.codice_fiscale  || '',
      titolo_studio:   contatto.titolo_studio   || '',
      recapiti: {
        email:     recapiti.email     || '',
        cellulare: recapiti.cellulare || '',
      },
      impieghi,
      presa_visione_informativa: '1',
      data_presa_visione:        (() => { const d = new Date(); return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 19); })(),
      url_informativa:           PRIVACY.urlInformativaConvegni,
    };

    const res = await saveAnagraficaCompleta(registrazione);
    if (res.success) {
      setNeedsConsentUpdate(false);
    } else {
      setSubmitError(res.message || 'Errore durante il salvataggio. Riprova.');
      setSubmitting(false);
    }
  }, [checked, user]);

  // Nessun utente autenticato, verifica ancora in corso, o consenso già aggiornato: nulla da mostrare
  if (!isAuthReady || !user || checking || !needsConsentUpdate) return null;

  return (
    <div className="fixed inset-0 z-40 bg-black bg-opacity-60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div>
            <h2 className="text-base font-bold text-gray-900 mb-1">Informativa privacy aggiornata</h2>
            <p className="text-sm text-gray-600">
              C'è stato un aggiornamento del trattamento dei dati personali riportato nell'
              <button type="button" onClick={handleOpenPrivacyModal} className="text-anutel hover:underline font-medium">
                informativa privacy
              </button>
              . Per continuare, prendine visione e conferma di averla letta.
            </p>
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer select-none mb-4">
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
          />
          <span className="text-sm text-gray-700">
            Dichiaro di aver preso visione degli aggiornamenti dell'informativa sul trattamento dei dati personali.
          </span>
        </label>

        {submitError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {submitError}
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={!checked || submitting}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {submitting ? 'Salvataggio...' : 'Conferma presa visione'}
          </button>
        </div>
      </div>
    </div>
  );
}
